/**
 * Centralized Date Utility for Ezy Fleet Management System
 * Standard format: DD-MM-YYYY
 */

/**
 * Format a date string or Date object to DD-MM-YYYY
 * @param {string|Date} date - Date to format (YYYY-MM-DD, ISO string, or Date object)
 * @returns {string} Formatted date string in DD-MM-YYYY format
 */
export const formatDateDisplay = (date) => {
  if (!date) return 'N/A';
  
  try {
    let dateStr = date;
    
    // Handle Date objects
    if (date instanceof Date) {
      if (isNaN(date.getTime())) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
    
    // Handle ISO datetime format (YYYY-MM-DDTHH:MM:SS)
    if (typeof dateStr === 'string' && dateStr.includes('T')) {
      dateStr = dateStr.split('T')[0];
    }
    
    // Handle YYYY-MM-DD format
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${day}-${month}-${year}`;
    }
    
    // Already in DD-MM-YYYY format (with or without zero-padding)
    if (typeof dateStr === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('-');
      return `${day.padStart(2, '0')}-${month.padStart(2, '0')}-${year}`;
    }
    
    // Try to parse as date
    const parsedDate = new Date(dateStr);
    if (!isNaN(parsedDate.getTime())) {
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const year = parsedDate.getFullYear();
      return `${day}-${month}-${year}`;
    }
    
    return 'N/A';
  } catch (e) {
    console.error('Date formatting error:', e);
    return 'N/A';
  }
};

// Alias for backward compatibility
export const formatDate = formatDateDisplay;

/**
 * Convert DD-MM-YYYY to YYYY-MM-DD for input fields and API calls
 * @param {string} dateStr - Date in DD-MM-YYYY or other format
 * @returns {string} Date in YYYY-MM-DD format
 */
export const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  
  // Already in YYYY-MM-DD format
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }
  
  // Handle ISO datetime format
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  
  // Convert DD-MM-YYYY to YYYY-MM-DD (handles single-digit days/months)
  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
    const [day, month, year] = dateStr.split('-');
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  
  return dateStr;
};

// Alias for API operations
export const toApiDateFormat = formatDateForInput;

/**
 * Format date and time to DD-MM-YYYY HH:MM
 * @param {string|Date} dateTime - DateTime to format
 * @param {string} time - Optional separate time string (HH:MM)
 * @returns {string} Formatted datetime string
 */
export const formatDateTimeDisplay = (dateTime, time = null) => {
  if (!dateTime) return 'N/A';
  
  try {
    // If time is provided separately
    if (time) {
      const formattedDate = formatDateDisplay(dateTime);
      return formattedDate !== 'N/A' ? `${formattedDate} ${time}` : 'N/A';
    }
    
    // Handle ISO datetime format
    if (typeof dateTime === 'string' && dateTime.includes('T')) {
      const [datePart, timePart] = dateTime.split('T');
      const formattedDate = formatDateDisplay(datePart);
      if (timePart && formattedDate !== 'N/A') {
        const timeOnly = timePart.split('.')[0].substring(0, 5); // HH:MM
        return `${formattedDate} ${timeOnly}`;
      }
      return formattedDate;
    }
    
    // Handle Date objects
    if (dateTime instanceof Date && !isNaN(dateTime.getTime())) {
      const formattedDate = formatDateDisplay(dateTime);
      const hours = String(dateTime.getHours()).padStart(2, '0');
      const minutes = String(dateTime.getMinutes()).padStart(2, '0');
      return `${formattedDate} ${hours}:${minutes}`;
    }
    
    return formatDateDisplay(dateTime);
  } catch (e) {
    console.error('DateTime formatting error:', e);
    return 'N/A';
  }
};

// Alias for consistency
export const formatDateTime = formatDateTimeDisplay;

/**
 * Get today's date in DD-MM-YYYY format
 * @returns {string} Today's date
 */
export const getTodayFormatted = () => {
  return formatDateDisplay(new Date());
};

/**
 * Get today's date in YYYY-MM-DD format (for input fields)
 * @returns {string} Today's date for input
 */
export const getTodayForInput = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const year = today.getFullYear();
  return `${year}-${month}-${day}`;
};

/**
 * Get current timestamp for reports in DD-MM-YYYY HH:MM format
 * @returns {string} Current timestamp
 */
export const getCurrentTimestamp = () => {
  const now = new Date();
  const date = formatDateDisplay(now);
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${date} ${hours}:${minutes}`;
};

/**
 * Format date for file names (DD_MM_YYYY)
 * @param {string|Date} date - Date to format (defaults to today)
 * @returns {string} Date formatted for file names
 */
export const formatDateForFilename = (date = new Date()) => {
  const formatted = formatDateDisplay(date);
  return formatted !== 'N/A' ? formatted.replace(/-/g, '_') : getTodayForInput().replace(/-/g, '_');
};

/**
 * Parse a date string and return a Date object
 * @param {string} dateStr - Date string in DD-MM-YYYY or YYYY-MM-DD format
 * @returns {Date|null} Parsed Date object or null if invalid
 */
export const parseDate = (dateStr) => {
  if (!dateStr) return null;
  
  try {
    // Handle DD-MM-YYYY format (with or without zero-padding)
    if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('-');
      return new Date(year, month - 1, day);
    }
    
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return new Date(year, month - 1, day);
    }
    
    // Try to parse as ISO date
    const date = new Date(dateStr);
    return !isNaN(date.getTime()) ? date : null;
  } catch (e) {
    return null;
  }
};

/**
 * Check if a date is expired (in the past)
 * @param {string|Date} date - Date to check
 * @returns {boolean} True if date is in the past
 */
export const isExpired = (date) => {
  const parsedDate = typeof date === 'string' ? parseDate(date) : date;
  if (!parsedDate) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsedDate < today;
};

/**
 * Check if a date is expiring soon (within given days)
 * @param {string|Date} date - Date to check
 * @param {number} days - Number of days threshold (default 30)
 * @returns {boolean} True if date is within threshold
 */
export const isExpiringSoon = (date, days = 30) => {
  const parsedDate = typeof date === 'string' ? parseDate(date) : date;
  if (!parsedDate) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + days);
  futureDate.setHours(23, 59, 59, 999);
  
  return parsedDate >= today && parsedDate <= futureDate;
};

/**
 * Get days until a date
 * @param {string|Date} date - Target date
 * @returns {number} Number of days (negative if in past)
 */
export const getDaysUntil = (date) => {
  const parsedDate = typeof date === 'string' ? parseDate(date) : date;
  if (!parsedDate) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsedDate.setHours(0, 0, 0, 0);
  
  const diffTime = parsedDate - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export default {
  formatDateDisplay,
  formatDate,
  formatDateForInput,
  toApiDateFormat,
  formatDateTimeDisplay,
  formatDateTime,
  getTodayFormatted,
  getTodayForInput,
  getCurrentTimestamp,
  formatDateForFilename,
  parseDate,
  isExpired,
  isExpiringSoon,
  getDaysUntil
};

/**
 * Truncate a name to show only the first N words
 * @param {string} name - Full name to truncate
 * @param {number} wordCount - Number of words to show (default: 2)
 * @returns {string} Truncated name
 */
export const truncateName = (name, wordCount = 2) => {
  if (!name) return 'N/A';
  const words = name.trim().split(/\s+/);
  if (words.length <= wordCount) return name;
  return words.slice(0, wordCount).join(' ');
};

/**
 * Truncate company/lessee name to first 2 words
 * @param {string} companyName - Full company name
 * @returns {string} Truncated company name
 */
export const truncateCompanyName = (companyName) => {
  return truncateName(companyName, 2);
};
